import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useCurrentUser } from '../../hooks/useCurrentUser'
import useApi from '../../hooks/useApi'
import ProfileTemplate from './ProfileTemplate'
import { ProfileMain } from '../../components/Profiles'
import { UserProfileLeftSidebar } from '../../components/Profiles/UserProfile'
import Loading from '../../components/loading'

// build socialLinks for ProfileHeader from user/business object
function buildSocialLinksFromUser(obj) {
  if (!obj) return []
  const platforms = [
    { key: 'facebook_link', platform: 'facebook' },
    { key: 'instagram_link', platform: 'instagram' },
    { key: 'linkedin_link', platform: 'linkedin' },
    { key: 'youtube_link', platform: 'youtube' },
    { key: 'tiktok_link', platform: 'tiktok' },
  ]
  return platforms
    .filter(({ key }) => obj[key])
    .map(({ platform, key }) => ({ platform, href: obj[key], label: platform }))
}

// current user's profile page.

export default function UserProfilePage() {
  const { t, i18n } = useTranslation()
  const { username: usernameParam } = useParams()
  const { currentUser } = useCurrentUser()
  const {
    getCurrentUser,
    getCurrentUserGroups,
    getGroup,
    getUser,
    getUserFollowers,
    getUserGroups,
    getTodaysEvents,
    getMonthlyEvents,
    userFollow,
    userUnfollow,
  } = useApi()

  const [user, setUser] = useState(null)
  const [groups, setGroups] = useState([])
  const [upcomingEvents, setUpcomingEvents] = useState([])
  const [followersCount, setFollowersCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [profileNotFound, setProfileNotFound] = useState(false)
  const [optimisticFollowing, setOptimisticFollowing] = useState(null)
  const [followLoading, setFollowLoading] = useState(false)

  const decodedUsername = usernameParam != null ? decodeURIComponent(usernameParam).replace(/^@/, '') : null
  const isOwnProfile =
    currentUser?.nickname != null &&
    decodedUsername != null &&
    decodedUsername === currentUser.nickname

  useEffect(() => {
    setOptimisticFollowing(null)
  }, [user?.id])

  const handleFollowClick = useCallback(async () => {
    const profileUser = user
    if (!profileUser?.id || isOwnProfile || followLoading) return
    const isFollowingNow = optimisticFollowing ?? profileUser?.current_user_follows ?? false
    const nextFollowing = !isFollowingNow
    setOptimisticFollowing(nextFollowing)
    setFollowLoading(true)
    try {
      const response = nextFollowing
        ? await userFollow({ id: profileUser.id })
        : await userUnfollow({ id: profileUser.id })
      const status = response?.status ?? response?.response?.status
      const success = status == null || (status >= 200 && status < 300)
      if (!success) setOptimisticFollowing(null)
      else setUser(prev => (prev ? { ...prev, current_user_follows: nextFollowing } : prev))
    } catch {
      setOptimisticFollowing(null)
    } finally {
      setFollowLoading(false)
    }
  }, [user, isOwnProfile, optimisticFollowing, followLoading, userFollow, userUnfollow])

  useEffect(() => {
    if (!decodedUsername) {
      setIsLoading(false)
      return
    }
    let cancelled = false
    setProfileNotFound(false)

    const run = async () => {
      try {
        if (isOwnProfile && currentUser?.id) {
          const [userRes, groupsRes, followersRes, todayRes, monthRes] = await Promise.allSettled([
            getCurrentUser(),
            getCurrentUserGroups(),
            getUserFollowers({ id: currentUser.id }),
            getTodaysEvents(),
            getMonthlyEvents(),
          ])
          if (cancelled) return
          if (userRes.status === 'fulfilled' && userRes.value?.data) {
            setUser(userRes.value.data)
          } else if (currentUser) {
            setUser(currentUser)
          }
          if (groupsRes.status === 'fulfilled' && groupsRes.value?.data) {
            const rawGroups = groupsRes.value.data
            const enriched = await Promise.all(
              rawGroups.map(async (g) => {
                try {
                  const { data } = await getGroup({ id: g.slug ?? g.id })
                  return { ...g, user_count: data?.user_count ?? g.user_count }
                } catch {
                  return g
                }
              })
            )
            if (!cancelled) setGroups(enriched)
          }
          let count = 0
          if (followersRes.status === 'fulfilled') {
            const data = followersRes.value?.data
            if (Array.isArray(data)) count = data.length
            else if (typeof data?.count === 'number') count = data.count
            else if (Array.isArray(data?.followers)) count = data.followers.length
          }
          if (count === 0 && userRes.status === 'fulfilled' && typeof userRes.value?.data?.followers_count === 'number') {
            count = userRes.value.data.followers_count
          }
          if (!cancelled) setFollowersCount(count)
          const today = todayRes.status === 'fulfilled' ? todayRes.value?.data ?? [] : []
          const month = monthRes.status === 'fulfilled' ? monthRes.value?.data ?? [] : []
          const combined = [...(Array.isArray(today) ? today : []), ...(Array.isArray(month) ? month : [])]
          if (!cancelled) setUpcomingEvents(combined.slice(0, 10))
        } else {
          const userRes = await getUser({ nickname: decodedUsername })
          if (cancelled) return
          if (userRes?.status !== 200 || !userRes?.data) {
            if (!cancelled) setProfileNotFound(true)
            if (!cancelled) setUser(null)
            return
          }
          const profileUser = userRes.data
          if (!cancelled) setUser(profileUser)
          const slugOrId = profileUser.slug ?? profileUser.id
          const [groupsRes, followersRes] = await Promise.allSettled([
            getUserGroups(slugOrId),
            getUserFollowers({ id: profileUser.id }),
          ])
          if (cancelled) return
          if (groupsRes.status === 'fulfilled' && groupsRes.value?.data) {
            const rawGroups = groupsRes.value.data
            const enriched = await Promise.all(
              rawGroups.map(async (g) => {
                try {
                  const { data } = await getGroup({ id: g.slug ?? g.id })
                  return { ...g, user_count: data?.user_count ?? g.user_count }
                } catch {
                  return g
                }
              })
            )
            if (!cancelled) setGroups(enriched)
          }
          let count = 0
          if (followersRes.status === 'fulfilled') {
            const data = followersRes.value?.data
            if (Array.isArray(data)) count = data.length
            else if (typeof data?.count === 'number') count = data.count
            else if (Array.isArray(data?.followers)) count = data.followers.length
          }
          if (count === 0 && typeof profileUser?.followers_count === 'number') {
            count = profileUser.followers_count
          }
          if (!cancelled) setFollowersCount(count)
        }
      } catch (err) {
        if (!cancelled) setProfileNotFound(true)
        if (!cancelled && isOwnProfile && currentUser) setUser(currentUser)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [decodedUsername, isOwnProfile, currentUser?.id, currentUser?.nickname])


  const displayUser = user ?? currentUser
  const profileUrl =
    displayUser?.nickname != null
      ? `${window.location.origin}/profile/${encodeURIComponent(displayUser.nickname)}`
      : null
  const onShareClick = useCallback(() => {
    if (!profileUrl) return
    navigator.clipboard.writeText(profileUrl).then(
      () => {
        if (typeof window !== 'undefined' && window.alert) {
          window.alert(t('share.copied', 'Profile link copied to clipboard.'))
        }
      },
      () => {}
    )
  }, [profileUrl, t])

  if (isLoading) {
    return <Loading show />
  }

  if (profileNotFound || (!user && !isOwnProfile)) {
    return (
      <div className="container py-5">
        <p className="text-muted">{t('profiles.userNotFound', 'User not found.')}</p>
      </div>
    )
  }

  const avatarUrl =
    displayUser?.media_attachment?.thumbnail_url ??
    displayUser?.media_attachment?.direct_upload_url ??
    '/images/default-user.png'
  const subtitle = displayUser?.nickname ? `@${displayUser.nickname}` : null

  // SETS DEFAULT - NEED TO UPDATE BUTTON TO WORK
  const coverImageUrl =
    displayUser?.cover_photo?.thumbnail_url ??
    displayUser?.cover_photo?.direct_upload_url ??
    undefined

  const socialLinks = buildSocialLinksFromUser(displayUser)
  const profileHref = profileUrl ?? '/profile'

  return (
    <ProfileTemplate
      profileType="user"
      headerProps={{
        name: displayUser?.name ?? null,
        avatarUrl,
        subtitle,
        coverImageUrl,
        onCoverPhotosClick: isOwnProfile
          ? () => {
              // TODO: open cover photo modal or navigate to settings when flow is ready
            }
          : undefined,
        isFollowing: (optimisticFollowing ?? displayUser?.current_user_follows) ?? false,
        onFollowClick: !isOwnProfile && displayUser?.id ? handleFollowClick : undefined,
        followLoading,
        onShareClick: profileUrl ? onShareClick : undefined,
        socialLinks,
      }}
      breadcrumbItems={[
        { label: t('profiles.profile', 'Profile'), href: profileHref },
        { label: displayUser?.name ?? t('profiles.you', 'You') },
      ]}
      leftSidebarContent={
        <UserProfileLeftSidebar
          name={displayUser?.name}
          location={displayUser?.location}
          quote={displayUser?.quote ?? displayUser?.user_quote ?? displayUser?.favorite_quote}
          quoteAuthor={displayUser?.quote_author ?? displayUser?.favorite_quote_author}
          followersCount={followersCount}
          onEditCommunity={undefined}
          favoriteCharity={displayUser?.favorite_charity}
          categories={displayUser?.categories}
          groups={groups}
          upcomingEvents={upcomingEvents}
          awards={[]}
          locale={displayUser?.language ?? i18n?.language ?? 'en'}
        />
      }
      mainContent={<ProfileMain location={displayUser?.location} />}
      rightSidebarContent={null}
    />
  )
}
